import { Form } from 'antd';
import BaseModal from 'basic-ui-lib/components/Modal/BaseModal';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import { setPageLoading } from 'widgets/PageLoading';
import { toast } from 'react-toastify';
import { getMessageError } from 'services/response';
import InputLarge from 'basic-ui-lib/components/Input/InputLarge';

interface EditNameModalProps {
  currentName: string;
  id: string;
  onFinish?: (id: string, name: string) => Promise<void> | void;
}

export interface EditNameModalRef {
  open: ({ currentName, id, onFinish }: EditNameModalProps) => void;
}
interface Props {}

interface FormValues {
  name: string;
}

const EditNameModal = forwardRef<EditNameModalRef, Props>(({}, ref) => {
  const [form] = Form.useForm();
  const [isOpen, setIsOpen] = useState(false);
  const [currentData, setCurrentData] = useState<EditNameModalProps | null>(null);

  const onFinishRef = useRef<((id: string, name: string) => Promise<void> | void) | null>(null);

  useImperativeHandle(ref, () => ({
    open: ({ currentName, id, onFinish }: EditNameModalProps) => {
      setCurrentData({ currentName, id });
      onFinishRef.current = onFinish || null;
      setIsOpen(true);
      form.setFieldsValue({ name: currentName });
    },
  }));

  const onClose = () => {
    setIsOpen(false);
    setCurrentData(null);
    onFinishRef.current = null;
    form.resetFields();
  };

  const handleSubmit = () => {
    form.submit();
  };

  const handleFinish = async (values: FormValues) => {
    if (!currentData) return;

    try {
      setPageLoading(true);

      if (onFinishRef.current) {
        await onFinishRef.current(currentData.id, values.name);
      }
      onClose();
    } catch (error) {
      toast.error(getMessageError(error) || 'An error occurred while updating the name.');
    } finally {
      setPageLoading(false);
    }
  };
  return (
    <BaseModal title='Edit Name' isOpen={isOpen} width={1000} onClose={onClose} okText='Ok' onOk={handleSubmit}>
      <Container>
        <Form layout='vertical' form={form} onFinish={handleFinish}>
          <Form.Item name='name' rules={[{ required: true, message: 'Please input name' }]}>
            <InputLarge placeholder='Please enter name' />
          </Form.Item>
        </Form>
      </Container>
    </BaseModal>
  );
});

EditNameModal.displayName = 'EditNameModal';
export default EditNameModal;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  padding: 0 50px;
`;
